import { z } from 'zod'

// Re-export existing types
export * from './locations'

// Re-export parking types from parking feature
export { ParkingHistoryEntrySchema, ParkingMetadataSchema } from '@/features/parking'
export type { ParkingHistoryEntry, ParkingMetadata } from '@/features/parking'

// Import for local use in AppDataSchema
import { ParkingHistoryEntrySchema } from '@/features/parking'

// App data schema (extends existing app_data structure)
export const AppDataSchema = z.object({
  location: z.string().optional(),
  import_comment: z.string().optional(),
  is_important_message: z.boolean().optional(),
  imported_at: z.string().optional(),
  edited_at: z.string().optional(),
  // Parking fields (current/latest parking)
  parked_at: z.string().optional(),
  parked_by: z.string().optional(),
  parked_reason: z.string().optional(),
  parked_lines: z.array(z.number()).optional(),
  parked_comment: z.string().optional(),
  parked_photo_url: z.string().url().optional(),
  parking_history: z.array(ParkingHistoryEntrySchema).optional(),
})

export type AppData = z.infer<typeof AppDataSchema>

// ============================================================================
// SPLIT ORDER TYPES
// ============================================================================

// Input for a single line in a split
export const SplitLineInputSchema = z.object({
  order_line_id: z.string().uuid(),
  line_id: z.number(), // ERP line number for display
  quantity: z.number().positive(),
  description: z.string().optional(),
})

export type SplitLineInput = z.infer<typeof SplitLineInputSchema>

// Input for creating a split order (delivery)
export const SplitOrderInputSchema = z.object({
  order_id: z.number(),
  lines: z.array(SplitLineInputSchema).min(1),
  location: z.string().min(1),
  comment: z.string().optional(),
  is_important_message: z.boolean().optional(),
  photo_url: z.string().url().optional(),
})

export type SplitOrderInput = z.infer<typeof SplitOrderInputSchema>

// Order line with remaining quantity info
export const OrderLineWithRemainingSchema = z.object({
  id: z.string().uuid(),
  line_id: z.number(),
  article_no: z.string().nullable(),
  description: z.string().nullable(),
  count: z.number(),
  delivered_count: z.number(),
  remaining: z.number(), // count - delivered_count
})

export type OrderLineWithRemaining = z.infer<typeof OrderLineWithRemainingSchema>

// Delivery summary for PartialOrderView
export const DeliverySummarySchema = z.object({
  id: z.string().uuid(),
  status: z.enum(['draft', 'ready', 'pending', 'delivered', 'failed']),
  location: z.string().nullable(),
  delivered_at: z.string().nullable(),
  created_at: z.string(),
  line_count: z.number(),
})

export type DeliverySummary = z.infer<typeof DeliverySummarySchema>

// Full partial order data for PartialOrderView
export const PartialOrderDataSchema = z.object({
  order: z.object({
    id: z.string().uuid(),
    order_id: z.number(),
    customer_name: z.string().nullable(),
    status: z.string(),
  }),
  pending_deliveries: z.array(DeliverySummarySchema),
  delivered_deliveries: z.array(DeliverySummarySchema),
  remaining_lines: z.array(OrderLineWithRemainingSchema),
  all_lines: z.array(OrderLineWithRemainingSchema),
})

export type PartialOrderData = z.infer<typeof PartialOrderDataSchema>
